/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {FC, useCallback, useEffect, useRef, useState} from 'react';
import {
  Grid,
  IconButton,
  makeStyles,
  Typography
} from '@material-ui/core';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import classnames from 'classnames';
import {
  format,
  getDate,
  isSameDay,
  isSameMonth,
  isToday,
  isWithinInterval
} from 'date-fns';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import {
  getDays,
  getHours,
  getMeridiem,
  getMinutes,
  resolveDateInput
} from '@ion/core/src/utils/DateUtil';
import {padStart} from '@ion/core/src/utils/StringUtil';
import {CTALink} from '../../components/buttons/CTALink';
import DigitalTimePicker from '../../components/date/DigitalTimePicker';
import Day from '../../components/daterangepicker/Day';
import {
  Meridiem,
  NavigationAction,
  TimeModificationAction
} from '../../components/daterangepicker/types';
import {
  chunks,
  getDaysInMonth
} from '../../components/daterangepicker/utils';
import Render from '../../render/Render';
import {toggleThemeStyle} from '../../theme/helpers';
import {dlsAlt, dlsColor, getStyleClass} from '../../theme/theme';
import Header from './Header';

// defines the styles used by the component
const useStyles = makeStyles(theme => ({
  root              : {},
  weekDaysContainer : {
    marginTop      : 10,
    display        : 'flex',
    justifyContent : 'space-around'
  },
  weekDaysText : {
    color         : dlsColor('grey1'),
    fontSize      : '12px',
    textAlign     : 'center',
    fontFamily    : 'Inter',
    fontWeight    : 500,
    lineHeight    : '16px',
    letterSpacing : '0.44px',
    verticalAlign : 'middle'
  },
  iconTop : {
    '&:hover' : {
      background : 'none'
    },
    transform : 'rotate(90deg)'
  },
  iconBottom : {
    '&:hover' : {
      background : 'none'
    },
    transform : 'rotate(270deg)'
  },
  daysContainer : {
    marginTop    : 15,
    marginBottom : 20
  },
  time : {
    display    : 'flex',
    alignItems : 'center',
    textAlign  : 'center',
    fontFamily : toggleThemeStyle('Neo Sans Std', 'Inter'),
    fontWeight : 400,
    fontSize   : '30px',
    lineHeight : '36px',
    padding    : '20px 0 20px 0'
  },
  meridiem : {
    cursor : 'pointer'
  },
  cta : {
    color     : dlsColor('grey3'),
    '&:hover' : {
      color : dlsColor('caBlue')
    },
    '&:select' : {
      color : dlsColor('caBlue')
    },
    cursor : 'pointer',
    ...dlsAlt('typography', 'cta2')
  },
  'current-cta' : {
    color         : dlsColor('darkestBlue'),
    cursor        : 'pointer',
    fontSize      : '13px',
    fontFamily    : 'Inter',
    fontWeight    : 600,
    lineHeight    : '16px',
    letterSpacing : '1px',
    verticalAlign : 'middle'
  },
  timeContainer : {
    display       : 'flex',
    flexDirection : 'column',
    alignItems    : 'center',
    padding       : '20 16 20 16'
  }
}));

// defines the prop definitions for the component
export interface MonthProps {
  value: Date;
  isDate?: boolean;
  isTime?: boolean;
  isDateTime?: boolean;
  setValue: (date: Date) => void;
  allowManualEntry?: boolean;
  manualInput?: string;
  handleClickAway: (val?: React.MouseEvent<HTMLElement>) => void;
  handlers: {
    onDayClick: (day: Date, hours: number, minutes: number) => void;
    onDayHover: (day: Date) => void;
    onMonthNavigate: (action: number) => void;
    onYearNavigate: (action: number) => void;
    onInputChange?: Function;
    onChangeDate?: Function;
  };
  isCurrentTheme?: boolean;
  hideClearButton: boolean;
  minDate?: Date;
  maxDate?: Date;
  actualValue?: Date;
  selectedDate?: Date;
};

enum Button {
  clear = 'clear',
  cancel = 'cancel',
  okay = 'okay'
};

// defines the legacy input format map...
const LegacyFormatMap = {
  [TimeModificationAction.HourUp] : (hours: number, min: number, med: Meridiem) => (
    {hours: hours !== 12 ? hours + 1 : 1, min, med}
  ),
  [TimeModificationAction.HourDown] : (hours: number, min: number, med: Meridiem) => (
    {hours: hours !== 1 ? hours - 1 : 12, min, med}
  ),
  [TimeModificationAction.MinuteUp] : (hours: number, min: number, med: Meridiem) => (
    {hours: hours, min: min !== 59 ? min + 1 : 0, med}
  ),
  [TimeModificationAction.MinuteDown] : (hours: number, min: number, med: Meridiem) => (
    {hours: hours, min: min !== 0 ? min - 1 : 59, med}
  ),
  [TimeModificationAction.ToggleMeridiem] : (hours: number, min: number, med: Meridiem) => (
    {hours, min, med: med === Meridiem.AM ? Meridiem.PM : Meridiem.AM}
  ),
  undefined : (hours: number, min: number, med: Meridiem) => (
    {hours, min, med}
  )
};

/**
 * @Month
 *
 * Defines the Month component.
 */
export const Month: FC<MonthProps> = (props: MonthProps) => {
  const classes = useStyles();
  const {
    handlers,
    isDate,
    isTime,
    maxDate,
    minDate,
    isDateTime,
    allowManualEntry,
    manualInput,
    value: date,
    actualValue,
    selectedDate,
    setValue: setDate,
    handleClickAway,
    hideClearButton = false,
    isCurrentTheme = false
  } = props;

  const {
    onMonthNavigate,
    onYearNavigate,
    onDayHover,
    onDayClick,
    onInputChange,
    onChangeDate
  } = handlers;

  const DAYS_OF_WEEK = translate('common:editors.dateTime.days', {returnObjects: true});

  const dateOriginalRef = useRef(date);
  const dateVal = date || (isCurrentTheme ? new Date() : resolveDateInput(new Date()));
  const getDisplayedHour = useCallback((date: Date): number => (
    isCurrentTheme ? date.getHours() : Number(getHours(date, ''))
  ), [isCurrentTheme]);
  const getDisplayedMinute = useCallback((date: Date): number => (
    isCurrentTheme ? date.getMinutes() : Number(getMinutes(date, ''))
  ), [isCurrentTheme]);
  const [hourVal, setHourVal]     = useState(getDisplayedHour(dateVal));
  const [minuteVal, setMinuteVal] = useState(getDisplayedMinute(dateVal));
  const legacyTimeRef = useRef({
    hour   : getDisplayedHour(dateVal),
    minute : getDisplayedMinute(dateVal)
  });
  const displayHour               = hourVal % 12 || 12;
  const displayMinute             = padStart(minuteVal, 0);
  const displayMeridiem           = hourVal >= 12 ? Meridiem.PM : Meridiem.AM;

  useEffect(() => {
    if (isCurrentTheme) {
      return;
    }
    if (legacyTimeRef.current.hour === hourVal && legacyTimeRef.current.minute === minuteVal) {
      return;
    }
    legacyTimeRef.current = {
      hour   : hourVal,
      minute : minuteVal
    };
    onDayClick(dateVal, hourVal, minuteVal);
  }, [
    dateVal,
    hourVal,
    isCurrentTheme,
    minuteVal,
    onDayClick
  ]);

  useEffect(() => {
    const nextHour = getDisplayedHour(dateVal);
    const nextMinute = getDisplayedMinute(dateVal);
    if (!isCurrentTheme) {
      legacyTimeRef.current = {
        hour   : nextHour,
        minute : nextMinute
      };
    }
    setHourVal(nextHour);
    setMinuteVal(nextMinute);
  }, [
    dateVal,
    getDisplayedHour,
    getDisplayedMinute,
    isCurrentTheme
  ]);

  const getClass = useCallback((name: string) => (
    getStyleClass(name, classes, isCurrentTheme)
  ), [isCurrentTheme, classes]);

  const resolveInput = useCallback((manualInput: string) => {
    const resolved = manualInput.split(' ');
    return isTime ? [null, ...resolved] : resolved;
  }, [isTime]);

  const onLegacyDateTimeChange = useCallback((action: TimeModificationAction) => {
    if (onInputChange) {
      const [date, time, md]  = resolveInput(manualInput);
      const [hr, mi]          = time.split(':');
      const {hours, min, med} = LegacyFormatMap[action]?.(parseInt(hr), parseInt(mi), md as Meridiem);
      const updated           = `${date ?? ''} ${hours}:${padStart(min, 0)} ${med}`.trim();
      onInputChange(updated);
    }
  }, [manualInput, onInputChange, resolveInput]);

  const bump = useCallback((what: number, bump: number, limit: number) => {
    const bumped = what + bump;
    return (bumped >= limit)
      ? (bumped - limit)
      : ((bumped < 0)
        ? (bumped + limit)
        : bumped);
  }, []);

  const onDefaultTimeChange = useCallback((action: TimeModificationAction) => {
    let hour   = hourVal;
    let minute = minuteVal;
    switch (action) {
      case TimeModificationAction.HourUp:
        hour = bump(hour, 1, 24);
        break;
      case TimeModificationAction.HourDown:
        hour = bump(hour, -1, 24);
        break;
      case TimeModificationAction.MinuteUp:
        minute = bump(minute, 1, 60);
        if (minute === 0) {
          hour = bump(hour, 1, 24);
        }
        break;
      case TimeModificationAction.MinuteDown:
        minute = bump(minute, -1, 60);
        if (minute === 59) {
          hour = bump(hour, -1, 24);
        }
        break;
      case TimeModificationAction.ToggleMeridiem:
        hour = bump(hour, 12, 24);
        break;
      default :
        break;
    }
    setHourVal(hour);
    setMinuteVal(minute);
    if (isCurrentTheme) {
      onDayClick(dateVal, hour, minute);
    }
  }, [
    bump,
    dateVal,
    hourVal,
    isCurrentTheme,
    minuteVal,
    onChangeDate,
    onDayClick,
    setDate
  ]);

  const getDateWithSelectedTime = useCallback((date: Date, hour: number, minute: number) => {
    const selectedDate = new Date(date);
    selectedDate.setHours(hour, minute, 0, 0);
    return selectedDate;
  }, []);

  const onChangeTime = useCallback((action: TimeModificationAction) => {
    if (allowManualEntry && manualInput) {
      onLegacyDateTimeChange(action);
    } else {
      onDefaultTimeChange(action);
    }
  }, [allowManualEntry, manualInput]);

  const onSelectTime = useCallback((hour: number, minute: number) => {
    const selectedDate = getDateWithSelectedTime(dateVal, hour, minute);
    setHourVal(hour);
    setMinuteVal(minute);
    onDayClick(selectedDate, hour, minute);
  }, [
    dateVal,
    getDateWithSelectedTime,
    onDayClick
  ]);

  const handleClick = useCallback(({target: {id}}) => {
    if (id !== Button.okay) {
      let date = null;
      if (id === Button.cancel) {
        date = dateOriginalRef.current;
        onChangeDate?.(null);
      }
      setDate(date);
      onChangeDate?.(date);
    } else {
      const isValidInterval = minDate && maxDate && !isNil(minDate) && !isNil(maxDate);
      const isOutOfRange    = isValidInterval ? !isWithinInterval(date, {start: minDate, end: maxDate}) : false;
      if (isOutOfRange) {
        onChangeDate?.(actualValue);
        setDate(actualValue);
      }
    }
    handleClickAway();
  }, [
    date,
    minDate,
    maxDate,
    onChangeDate,
    handleClickAway,
    setDate,
    actualValue
  ]);

  return (
    <Grid container>
      {(isDate || isDateTime) && (
        <>
          <Header date={dateVal}
            onMonthNavigate={onMonthNavigate}
            onYearNavigate={onYearNavigate}
            onPrevious={function() {
              onMonthNavigate(NavigationAction.Previous);
            }}
            onNext={function() {
              onMonthNavigate(NavigationAction.Next);
            }} />
          <Grid item container
            direction='row'
            justifyContent='space-between'
            className={classes.weekDaysContainer}>
            {DAYS_OF_WEEK.map((day) => (
              <Typography className={classes.weekDaysText} key={day} variant='caption'>
                {day}
              </Typography>
            ))}
          </Grid>
          <Grid item container
            direction='column'
            justifyContent='space-between'
            className={classes.daysContainer}>
            {chunks(getDaysInMonth(dateVal), 7).map((week, idx) => (
              <Grid justifyContent='space-between' wrap='nowrap' key={`dateFilter-week-${idx}`} container direction='row'>
                {week.map((day) => {
                  const isSelectedDay = selectedDate
                    ? (!allowManualEntry ? isSameDay(day, selectedDate) : getDays(selectedDate) === format(day, 'dd'))
                    : (!allowManualEntry ? isSameDay(day, dateVal) : getDays(dateVal) === format(day, 'dd'));
                  const formatted = isCurrentTheme
                    ? `${format(day, 'MM-dd-yyyy')} ${padStart(displayHour, 0)}:${displayMinute} ${displayMeridiem}`
                    : `${format(day, 'MM-dd-yyyy')} ${getHours(dateVal, 'hh')}:${getMinutes(dateVal, 'mm')} ${getMeridiem(dateVal)}`;
                  const isValidInterval = minDate && maxDate && !isNil(minDate) && !isNil(maxDate);
                  const isOutOfRange = isValidInterval ? !isWithinInterval(day, {start: minDate, end: maxDate}) : false;
                  return (
                    <Day key={format(day, 'MM-dd-yyyy')}
                      fromDateTime
                      outlined={isToday(day)}
                      filled={isSelectedDay}
                      disabled={
                        !isSameMonth(dateVal, day) || isOutOfRange
                      }
                      onClick={function() {
                        const resolved = !isCurrentTheme ? resolveDateInput(formatted) : day;
                        onDayClick(
                          resolved,
                          hourVal,
                          minuteVal
                        );
                      }}
                      onHover={function() { onDayHover(day); }}
                      noVisibilityOnDisabled={false}
                      value={getDate(day)} />
                  );
                })}
              </Grid>
            ))}
          </Grid>
        </>)}
      {(isTime || isDateTime) && !isCurrentTheme && (
        <Grid item
          container
          direction='row' style={{marginBottom: '20px'}}
          justifyContent='space-between'>
          <div className={classes.time} />
          <div className={classes.timeContainer}>
            <IconButton className={classes.iconTop}
              onClick={function() { onChangeTime(TimeModificationAction.HourUp); }}
              size='small'>
              <ChevronLeft />
            </IconButton>
            <span className={classes.time}>
              {getHours(dateVal, 'h')}
            </span>
            <IconButton className={classes.iconBottom}
              onClick={function() { onChangeTime(TimeModificationAction.HourDown); }}
              size='small'>
              <ChevronLeft />
            </IconButton>
          </div>
          <div className={classes.time}>:</div>
          <div className={classes.timeContainer}>
            <IconButton className={classes.iconTop}
              onClick={function() { onChangeTime(TimeModificationAction.MinuteUp); }}
              size='small'>
              <ChevronLeft />
            </IconButton>
            <span className={classes.time}>
              {getMinutes(dateVal, 'mm')}
            </span>
            <IconButton className={classes.iconBottom}
              onClick={function() { onChangeTime(TimeModificationAction.MinuteDown); }}
              size='small'>
              <ChevronLeft />
            </IconButton>
          </div>
          <div className={classes.time} />
          <div className={classes.timeContainer} style={{justifyContent: 'center'}}>
            <span className={classnames(classes.time, classes.meridiem)}
              onClick={function() { onChangeTime(TimeModificationAction.ToggleMeridiem); }}>
              {getMeridiem(dateVal)}
            </span>
          </div>
          <div className={classes.time} />
        </Grid>)}
      <Render.If condition={(isTime || isDateTime) && isCurrentTheme}>
        <Grid item container
          direction='row'
          justifyContent='space-between'
          style={{marginBottom: '20px'}}>
          <DigitalTimePicker dateValue={getDateWithSelectedTime(dateVal, hourVal, minuteVal)}
            onChangeTime={onDefaultTimeChange}
            onSelectTime={onSelectTime} />
        </Grid>
      </Render.If>
      <Grid item container
        direction='row'
        justifyContent='flex-end'
        style={{padding: '0 16px 16px'}}>
        <Render.If condition={!hideClearButton}>
          <CTALink id={Button.clear}
            className={getClass('cta')}
            style={{marginRight: '25px'}}
            onClick={handleClick}>
            {translate('common:editors.dateTime.clear')}
          </CTALink>
        </Render.If>
        <CTALink id={Button.cancel} className={getClass('cta')} style={{marginRight: '25px'}} onClick={handleClick}>
          {translate('common:editors.dateTime.cancel')}
        </CTALink>
        <CTALink id={Button.okay} className={getClass('cta')} onClick={handleClick}>
          {translate('common:editors.dateTime.okay')}
        </CTALink>
      </Grid>
    </Grid>
  );
};

export default Month;
